import * as React from 'react'

const STYLESHEET: { current: CSSStyleSheet | undefined } = { current: undefined }
const INSERTED_RULES = new Map<string, { count: number }>()

/**
 * Do nothing with the template, used only to trigger syntax highlight
 * @deprecated
 */
export function css(
  statics: TemplateStringsArray,
  ...interpolations: (string | number | undefined | null | boolean)[]
) {
  return compile({}, statics, interpolations)
}

export function compile<PROPS = {}>(
  props: PROPS,
  statics: TemplateStringsArray,
  interpolations: (
    | string
    | number
    | undefined
    | null
    | boolean
    | ((props: PROPS) => string | number | undefined | null | boolean)
  )[],
) {
  const rule = []
  for (let i = 0; i < interpolations.length; i++) {
    rule.push(statics[i])
    let interpolation = interpolations[i]
    if (typeof interpolation === 'function') {
      interpolation = interpolation(props)
    }

    if (typeof interpolation !== 'boolean' && interpolation !== undefined && interpolation !== null) {
      rule.push(String(interpolation))
    }
  }
  rule.push(statics[statics.length - 1])

  return rule.join('')
}

export const keyframes = css

/**
 * Insert the given css rules in a global stylesheet. Returns the generated
 * className. You can pass an optional `hint` that will be used in the className
 * generation to help you find the generated rule in the stylesheet or debugger.
 *
 * The className is generated with a hash of the rule to avoid collisions and
 * duplications.
 *
 * No effort is done to handle rule ordering, rules are inserted in call order.
 * This hook should be used as a last resort.
 *
 * No effort is done to handle nesting
 * @deprecated
 */
export function useDramaticallyInefficientScopedCss(ruleContent: string, hint = 'rule') {
  const id = getUniqueId(ruleContent, hint)

  useInjectCss(() => injectCss(getRuleSet(ruleContent, `.${id}`), id))

  return id
}

/** @deprecated */
export function useDramaticallyInefficientGlobalCss(rule: string) {
  const id = getUniqueId(rule)

  useInjectCss(() => injectCss(rule, id))

  return id
}

/** @deprecated */
export function useDramaticallyInefficientScopedMediaQuery(query: string, content: string, hint = 'rule') {
  const id = getUniqueId(`@media ${query} { ${content} }`, hint)

  useInjectCss(() => injectCss(getMediaQueryRule(content, `.${id}`, query), id))

  return id
}

/** @deprecated */
export function useDramaticallyInefficientScopedKeyframes(content: string, hint = 'rule') {
  const id = getUniqueId(`@keyframes { ${content} }`, hint)

  useInjectCss(() => injectCss(getKeyframeRule(content, id), id))

  return id
}

function useInjectCss(inject: () => () => void) {
  React.useLayoutEffect(() => {
    return inject()
  }, [])
}

/** @deprecated */
export function injectCss(rule: string, id: string) {
  const ruleMeta = INSERTED_RULES.get(id)

  if (ruleMeta === undefined) {
    if (STYLESHEET.current === undefined) {
      const style = document.createElement('style')
      style.setAttribute('data-dynamic-css', 'true')
      document.head.appendChild(style)
      STYLESHEET.current = style.sheet!
    }

    STYLESHEET.current.insertRule(rule, STYLESHEET.current.cssRules.length)
    INSERTED_RULES.set(id, { count: 1 })
  } else {
    ruleMeta.count++
  }

  return () => {
    if (!STYLESHEET.current) {
      return
    }

    const ruleMeta = INSERTED_RULES.get(id)!

    ruleMeta.count -= 1
    if (ruleMeta.count <= 0) {
      const rules = STYLESHEET.current.cssRules
      for (let i = 0; i < rules.length; i++) {
        if (rules[i].cssText === rule) {
          STYLESHEET.current.deleteRule(i)
          break
        }
      }
    }
  }
}

function getRuleSet(content: string, selector: string) {
  return `${selector}{${content}}`
}

function getMediaQueryRule(content: string, selector: string, query: string) {
  return `@media ${query} {${getRuleSet(content, selector)}}`
}

function getKeyframeRule(content: string, name: string) {
  return `@keyframes ${name}{${content}}`
}

function getUniqueId(rule: string, hint?: string) {
  const ruleHash = createHash()
    .update(hint ?? '')
    .update(rule)
    .digest('number')
    .toString(36)
  return `${hint}-${ruleHash}`
}

interface Hash {
  update(str: string): Hash
  digest(type: 'hex'): string
  digest(type: 'number'): number
  digest(type: 'number' | 'hex'): number | string
}

export function createHash(): Hash {
  let state = 0

  const hash = {
    update,
    digest,
  }

  return hash

  function update(str: string) {
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      state = (state << 5) - state + char
      state = state & state // Convert to 32bit integer
    }
    return hash
  }

  function digest(type: 'hex'): string
  function digest(type: 'number'): number
  function digest(type: 'hex' | 'number'): string | number {
    if (type === 'hex') {
      return Math.abs(state).toString(16)
    } else {
      return Math.abs(state)
    }
  }
}
